import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, Button, Container } from '@mui/material';
import { Videocam as VideocamIcon } from '@mui/icons-material';
import TwilioVideoRoom from './TwilioVideoRoom';
import { useUser } from '../../contexts/UserContext';
import { useVideoCall } from '../../contexts/VideoCallContext';
import axios from 'axios';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

interface RoomInfo {
   roomSid: string;
   roomName: string;
   eventTitle: string;
   status: string;
   isActive: boolean;
   status_message?: string;
}

const JoinRoomPage: React.FC = () => {
   const { roomId } = useParams<{ roomId: string }>();
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const { videoCallState, startVideoCall, endVideoCall, isOnEventPage } = useVideoCall();

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
   const [eventDetails, setEventDetails] = useState<any>(null);

   const [searchParams] = useSearchParams();
   const eventId = searchParams.get('eventId');

   // Check if we already have an active video call for this room
   const hasActiveCall = videoCallState.isActive && videoCallState.roomId === roomId;

   // Extract room information from URL parameter
   useEffect(() => {
      const fetchRoomInfo = async () => {
         if (!roomId) {
            setError('Invalid room URL');
            setLoading(false);
            return;
         }

         try {
            setLoading(true);

            // Try to get room information from the backend
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const response = await axios.get(
               `${process.env.REACT_APP_API_URL}/api/twilio/room/${roomId}`,
               { headers }
            );

            if (response.data && response.data.room) {
               const room = response.data.room;
               setRoomInfo({
                  roomSid: room.sid,
                  roomName: room.uniqueName,
                  eventTitle: room.uniqueName.replace(/_/g, ' '), // Convert underscores back to spaces
                  status: room.status,
                  isActive: true,
                  status_message: room.status,
               });
            } else {
               throw new Error('Room not found');
            }
         } catch (err: any) {
            console.error('Error fetching room info:', err);
            setError('Room not found or no longer available');
         } finally {
            setLoading(false);
         }
      };

      fetchRoomInfo();
   }, [roomId]);

   useEffect(() => {
      const fetchEventDetails = async () => {
         try {
            if (!eventId) {
               setError('Event not found');
               return;
            }
            const eventRef = doc(db, 'events', eventId);
            const eventDoc = await getDoc(eventRef);
            if (eventDoc.exists()) {
               setEventDetails(eventDoc.data());
            }
         } catch (err: any) {
            setError('Failed to fetch event details');
         }
      };
      fetchEventDetails();
   }, [eventId]);

   const handleJoinRoom = () => {
      if (!currentUser || !roomInfo || !eventId) {
         setError('Please log in to join the video call');
         return;
      }

      // Start the video call using the global context
      startVideoCall({
         roomName: roomInfo.roomName,
         eventTitle: roomInfo.eventTitle,
         eventId: eventId,
         roomId: roomId!,
         role: eventDetails?.owner === currentUser?.uid ? 'host' : 'participant',
         identity: currentUser?.displayName || currentUser?.email || 'Anonymous',
      });
   };

   const handleLeaveRoom = () => {
      endVideoCall();
      navigate('/dashboard');
   };

   const handleError = (error: Error) => {
      setError(`Failed to join room: ${error.message}`);
   };

   if (loading) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
               <CircularProgress sx={{ mb: 2 }} />
               <Typography>Loading event information...</Typography>
            </Box>
         </Container>
      );
   }

   if (error) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
            <Button variant='contained' onClick={() => navigate('/dashboard')}>
               Back to Dashboard
            </Button>
         </Container>
      );
   }

   if (!roomInfo) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Alert severity='warning'>Event information not available</Alert>
         </Container>
      );
   }

   // If we're on the event page and have an active call, show fullscreen
   if (hasActiveCall && isOnEventPage) {
      return (
         <Container maxWidth='xl' sx={{ py: 2 }}>
            <TwilioVideoRoom
               role={videoCallState.role}
               roomName={videoCallState.roomName}
               eventTitle={videoCallState.eventTitle}
               onLeave={handleLeaveRoom}
               onError={handleError}
               isPopupMode={false}
            />
         </Container>
      );
   }

   // If we have an active call but we're not on the event page, show notice
   if (hasActiveCall && !isOnEventPage) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant='h5' gutterBottom>
                  You're already in this video call
               </Typography>
               <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                  The video call is active in popup mode. It will return to fullscreen when you're
                  on this page.
               </Typography>
               <Button variant='contained' onClick={() => window.location.reload()}>
                  Refresh to see fullscreen
               </Button>
            </Paper>
         </Container>
      );
   }

   // Show the join room interface
   return (
      <Container maxWidth='md' sx={{ py: 4 }}>
         <Paper sx={{ p: 4, textAlign: 'center' }}>
            <VideocamIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />

            <Typography variant='h4' gutterBottom>
               {roomInfo.eventTitle}
            </Typography>

            <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
               You're about to join a virtual event
            </Typography>

            <Box sx={{ mb: 3 }}>
               {currentUser && (
                  <Typography variant='body2'>
                     <strong>Joining as:</strong>{' '}
                     {currentUser.displayName || currentUser.email || 'User'}
                  </Typography>
               )}
            </Box>

            {!currentUser ? (
               <Alert severity='warning' sx={{ mb: 3 }}>
                  Please log in to join this event
               </Alert>
            ) : !roomInfo.isActive ? (
               <Alert severity='info' sx={{ mb: 3 }}>
                  This event is not currently active. The host may need to start the event.
               </Alert>
            ) : null}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
               <Button
                  variant='contained'
                  size='large'
                  startIcon={<VideocamIcon />}
                  onClick={handleJoinRoom}
                  disabled={!currentUser || !roomInfo.isActive}
               >
                  Join Event
               </Button>

               <Button variant='outlined' size='large' onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
               </Button>
            </Box>

            <Typography variant='caption' display='block' sx={{ mt: 3, color: 'text.secondary' }}>
               Make sure your camera and microphone are ready before joining
            </Typography>
         </Paper>
      </Container>
   );
};

export default JoinRoomPage;
